import Vue from 'vue'
import VueRouter from 'vue-router'
import { authState, restoreSession } from '@/services/auth'
import { adminDefaultPath, canAccessAdminRoute } from '@/services/adminAccess'

Vue.use(VueRouter)

const appTitle = '阅泽AI · 一站式AI视觉内容生产平台｜AI绘画、AI图片生成器、AI商品图、AI海报、AI模特图、AI电商设计、商品主图、详情页、海报设计、短视频素材生成'

const routes = [
  { path: '/', redirect: '/home' },
  {
    path: '/oauth2/authorize',
    component: () => import('@/views/OAuthAuthorize'),
    meta: { title: '应用授权', standaloneLayout: true },
  },
  {
    path: '/platform/invitations/accept',
    component: () => import('@/views/InvitationAccept'),
    meta: { title: '接受公司邀请', standaloneLayout: true },
  },
  { path: '/home', component: () => import('@/views/Home'), meta: { title: '首页' } },
  { path: '/community', component: () => import('@/views/CommunityFeed'), meta: { title: '社区' } },
  { path: '/community/post/:id', component: () => import('@/views/CommunityPostDetail'), meta: { title: '作品详情' } },
  { path: '/community/user/:userId', component: () => import('@/views/PersonalCenter'), meta: { title: '作者主页' } },
  { path: '/community/publish', component: () => import('@/views/CommunityPublish'), meta: { title: '发布作品' } },
  { path: '/messages', component: () => import('@/views/MessageCenter'), meta: { title: '消息中心' } },
  {
    path: '/developer',
    component: () => import('@/views/DeveloperCenter'),
    meta: { title: '开发者中心', requiresAuth: true },
  },
  { path: '/ai-image', component: () => import('@/views/AiImage'), meta: { title: '图像AI生成' } },
  { path: '/ai-edit', component: () => import('@/views/AiEdit'), meta: { title: '图像AI编辑' } },
  { path: '/scene', component: () => import('@/views/Scene'), meta: { title: '应用场景' } },
  { path: '/product-retouch', component: () => import('@/views/ProductRetouch'), meta: { title: '产品精修' } },
  { path: '/watermark-remove', component: () => import('@/views/WatermarkRemove'), meta: { title: '图片去水印' } },
  { path: '/cutout', component: () => import('@/views/Cutout'), meta: { title: '智能抠图' } },
  { path: '/model-generate', component: () => import('@/views/ModelGenerate'), meta: { title: '模特生成' } },
  { path: '/product-replace', component: () => import('@/views/ProductReplace'), meta: { title: '商品替换' } },
  { path: '/clothes-replace', component: () => import('@/views/ClothesReplace'), meta: { title: '服装替换' } },
  { path: '/tools/product-showcase', component: () => import('@/views/tools/ecom/ProductShowcase'), meta: { title: '商品展示' } },
  { path: '/tools/brand-style-product', component: () => import('@/views/tools/ecom/BrandStyleProduct'), meta: { title: '复刻大牌同款商品图' } },
  { path: '/tools/product-multi-angle', component: () => import('@/views/tools/ecom/ProductMultiAngle'), meta: { title: '商品多角度展示' } },
  { path: '/tools/model-tryon', component: () => import('@/views/tools/ecom/ModelTryon'), meta: { title: '模特试用' } },
  { path: '/tools/model-scene-design', component: () => import('@/views/tools/ecom/ModelSceneDesign'), meta: { title: '模特场景设计' } },
  { path: '/tools/outfit-flat-extract', component: () => import('@/views/tools/ecom/OutfitFlatExtract'), meta: { title: '提取穿搭平铺视角' } },
  { path: '/tools/product-explode', component: () => import('@/views/tools/ecom/ProductExplode'), meta: { title: '产品分解图' } },
  { path: '/tools/product-bg-replace', component: () => import('@/views/tools/ecom/ProductBgReplace'), meta: { title: '产品换背景' } },
  { path: '/tools/ai-retouch', component: () => import('@/views/tools/edit/AiRetouch'), meta: { title: 'AI修图' } },
  { path: '/tools/image-deblur', component: () => import('@/views/tools/edit/ImageDeblur'), meta: { title: '模糊修复' } },
  { path: '/tools/bg-blur', component: () => import('@/views/tools/edit/BgBlur'), meta: { title: '背景虚化' } },
  { path: '/tools/id-photo-grid', component: () => import('@/views/tools/edit/IdPhotoGrid'), meta: { title: '证件照' } },
  { path: '/tools/light-color-adjust', component: () => import('@/views/tools/edit/LightColorAdjust'), meta: { title: '光线色彩' } },
  { path: '/tools/image-content-edit', component: () => import('@/views/tools/edit/ImageContentEdit'), meta: { title: '内容编辑' } },
  { path: '/tools/image-merge', component: () => import('@/views/tools/edit/ImageMerge'), meta: { title: '多图融合' } },
  { path: '/tools/subject-extract', component: () => import('@/views/tools/edit/SubjectExtract'), meta: { title: '主体提取' } },
  { path: '/tools/photo-beauty', component: () => import('@/views/tools/edit/PhotoBeauty'), meta: { title: '照片美颜' } },
  { path: '/tools/clutter-remove', component: () => import('@/views/tools/edit/ClutterRemove'), meta: { title: '消除杂物' } },
  { path: '/tools/poster-design', component: () => import('@/views/tools/graphic/PosterDesign'), meta: { title: '海报设计' } },
  { path: '/tools/ip-to-3d', component: () => import('@/views/tools/graphic/IpTo3d'), meta: { title: '立体图' } },
  { path: '/tools/material-create', component: () => import('@/views/tools/graphic/MaterialCreate'), meta: { title: '素材制作' } },
  { path: '/tools/lineart-colorize', component: () => import('@/views/tools/graphic/LineartColorize'), meta: { title: '线稿上色' } },
  { path: '/tools/flat-to-packaging', component: () => import('@/views/tools/graphic/FlatToPackaging'), meta: { title: '包装效果' } },
  { path: '/tools/subject-migrate', component: () => import('@/views/tools/graphic/SubjectMigrate'), meta: { title: '主体迁移' } },
  { path: '/tools/illustration-design', component: () => import('@/views/tools/graphic/IllustrationDesign'), meta: { title: '插画设计' } },
  { path: '/tools/lineart-to-building', component: () => import('@/views/tools/decor/LineartToBuilding'), meta: { title: '建筑效果' } },
  { path: '/tools/object-placement', component: () => import('@/views/tools/decor/ObjectPlacement'), meta: { title: '物品摆放' } },
  { path: '/tools/lineart-to-interior', component: () => import('@/views/tools/decor/LineartToInterior'), meta: { title: '装修效果' } },
  { path: '/tools/showroom-render', component: () => import('@/views/tools/decor/ShowroomRender'), meta: { title: '展厅效果' } },
  { path: '/tools/rough-to-luxury', component: () => import('@/views/tools/decor/RoughToLuxury'), meta: { title: '精装效果' } },
  { path: '/tools/decor-style-clone', component: () => import('@/views/tools/decor/DecorStyleClone'), meta: { title: '风格复刻' } },
  { path: '/tools/building-multi-angle', component: () => import('@/views/tools/decor/BuildingMultiAngle'), meta: { title: '建筑多角度' } },
  { path: '/tools/wedding-scene-render', component: () => import('@/views/tools/decor/WeddingSceneRender'), meta: { title: '婚礼效果' } },
  { path: '/tools/hair-style-change', component: () => import('@/views/tools/portrait/HairStyleChange'), meta: { title: '换发型' } },
  { path: '/tools/outfit-change', component: () => import('@/views/tools/portrait/OutfitChange'), meta: { title: '换装' } },
  { path: '/tools/portrait-beauty', component: () => import('@/views/tools/portrait/PortraitBeauty'), meta: { title: '美颜' } },
  { path: '/tools/photo-to-figure-3d', component: () => import('@/views/tools/portrait/PhotoToFigure3d'), meta: { title: '手办建模' } },
  { path: '/tools/photo-to-figure', component: () => import('@/views/tools/logo/PhotoToFigure'), meta: { title: '图片转手办' } },
  { path: '/tools/merch-create', component: () => import('@/views/tools/logo/MerchCreate'), meta: { title: '周边创作' } },
  { path: '/tools/package-design', component: () => import('@/views/tools/logo/PackageDesign'), meta: { title: '包装设计' } },
  { path: '/tools/font-style-switch', component: () => import('@/views/tools/logo/FontStyleSwitch'), meta: { title: '字体风格' } },
  { path: '/tools/logo-visual', component: () => import('@/views/tools/logo/LogoVisual'), meta: { title: 'Logo视觉' } },
  { path: '/tools/ip-multi-angle', component: () => import('@/views/tools/logo/IpMultiAngle'), meta: { title: 'IP多视角' } },
  { path: '/tools/sketch-to-ip', component: () => import('@/views/tools/logo/SketchToIp'), meta: { title: '手绘转IP' } },
  { path: '/tools/gift-box-design', component: () => import('@/views/tools/cultural/GiftBoxDesign'), meta: { title: '礼盒设计' } },
  { path: '/tools/ip-merch-set', component: () => import('@/views/tools/cultural/IpMerchSet'), meta: { title: 'IP周边套装' } },
  { path: '/tools/cultural-poster', component: () => import('@/views/tools/cultural/CulturalPoster'), meta: { title: '文创海报' } },
  { path: '/tools/booth-material-set', component: () => import('@/views/tools/cultural/BoothMaterialSet'), meta: { title: '展位物料' } },
  { path: '/tools/packaging-unfold', component: () => import('@/views/tools/cultural/PackagingUnfold'), meta: { title: '包装展开图' } },
  { path: '/tools/museum-cultural', component: () => import('@/views/tools/cultural/MuseumCultural'), meta: { title: '博物馆文创' } },
  { path: '/tools/festival-material', component: () => import('@/views/tools/cultural/FestivalMaterial'), meta: { title: '节日物料' } },
  { path: '/tools/product-display', component: () => import('@/views/tools/cultural/ProductDisplay'), meta: { title: '产品陈列' } },
  { path: '/tools/booth-layout', component: () => import('@/views/tools/cultural/BoothLayout'), meta: { title: '摊位布置' } },
  { path: '/tools/hang-tag-design', component: () => import('@/views/tools/cultural/HangTagDesign'), meta: { title: '吊卡标签' } },
  { path: '/tools/ticket-visual', component: () => import('@/views/tools/cultural/TicketVisual'), meta: { title: '票券设计' } },
  { path: '/tools/tote-bag-design', component: () => import('@/views/tools/cultural/ToteBagDesign'), meta: { title: '手提袋设计' } },
  { path: '/tools/postcard-series', component: () => import('@/views/tools/cultural/PostcardSeries'), meta: { title: '明信片设计' } },
  { path: '/tools/badge-sticker-design', component: () => import('@/views/tools/cultural/BadgeStickerDesign'), meta: { title: '徽章贴纸' } },
  { path: '/tools/collab-concept', component: () => import('@/views/tools/cultural/CollabConcept'), meta: { title: '联名概念' } },
  { path: '/tools/landscape-enhance', component: () => import('@/views/tools/landscape/LandscapeEnhance'), meta: { title: '风景增强' } },
  { path: '/tools/sky-replace', component: () => import('@/views/tools/landscape/SkyReplace'), meta: { title: '天空替换' } },
  { path: '/tools/season-switch', component: () => import('@/views/tools/landscape/SeasonSwitch'), meta: { title: '季节切换' } },
  { path: '/tools/day-night-switch', component: () => import('@/views/tools/landscape/DayNightSwitch'), meta: { title: '昼夜切换' } },
  { path: '/tools/landscape-poster', component: () => import('@/views/tools/landscape/LandscapePoster'), meta: { title: '风景海报' } },
  { path: '/tools/scenic-promo', component: () => import('@/views/tools/landscape/ScenicPromo'), meta: { title: '景点宣传' } },
  { path: '/tools/landscape-stylize', component: () => import('@/views/tools/landscape/LandscapeStylize'), meta: { title: '风景风格化' } },
  { path: '/tools/tour-map-beautify', component: () => import('@/views/tools/landscape/TourMapBeautify'), meta: { title: '导览图美化' } },
  { path: '/tools/arch-landscape-merge', component: () => import('@/views/tools/landscape/ArchLandscapeMerge'), meta: { title: '建筑融景' } },
  { path: '/tools/weather-preview', component: () => import('@/views/tools/landscape/WeatherPreview'), meta: { title: '天气预览' } },
  { path: '/tools/travel-checkin', component: () => import('@/views/tools/landscape/TravelCheckin'), meta: { title: '旅行打卡' } },
  { path: '/tools/chinese-landscape', component: () => import('@/views/tools/landscape/ChineseLandscape'), meta: { title: '山水国风' } },
  { path: '/tools/landscape-expand', component: () => import('@/views/tools/landscape/LandscapeExpand'), meta: { title: '全景扩图' } },
  { path: '/tools/lake-reflection', component: () => import('@/views/tools/landscape/LakeReflection'), meta: { title: '倒影优化' } },
  { path: '/tools/landscape-postcard', component: () => import('@/views/tools/landscape/LandscapePostcard'), meta: { title: '风景明信片' } },
  { path: '/personal-center', component: () => import('@/views/PersonalCenter'), meta: { title: '个人中心' } },
  { path: '/user-detail', component: () => import('@/views/UserDetail'), meta: { title: '用户详情' } },
  { path: '/creation-center', component: () => import('@/views/CreationCenter'), meta: { title: '创作中心' } },
  { path: '/inspire-assistant', component: () => import('@/views/InspireAssistant'), meta: { title: '灵感助手' } },
  { path: '/inspire-assistant/chat', component: () => import('@/views/InspireAssistantChat'), meta: { title: '灵感助手对话' } },
  { path: '/prompt-library', component: () => import('@/views/PromptLibrary'), meta: { title: '提示词大全' } },
  { path: '/apps-detail', component: () => import('@/views/AppsDetail'), meta: { title: '应用详情' } },
  { path: '/video-generate', component: () => import('@/views/VideoGenerate'), meta: { title: '视频生成' } },
  { path: '/work-detail', component: () => import('@/views/WorkDetail'), meta: { title: '作品详情' } },
  { path: '/team-project', component: () => import('@/views/TeamProject'), meta: { title: '团队项目' } },
  { path: '/team-detail', component: () => import('@/views/TeamDetail'), meta: { title: '团队详情' } },
  { path: '/marketing-canvas', component: () => import('@/views/ProductDetailStudio'), meta: { title: '商品详情图工作台' } },
  { path: '/goods-detail', component: () => import('@/views/GoodsDetail'), meta: { title: '商品详情' } },
  {
    path: '/admin/operation-reports',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '运营报告', reportKey: 'overview', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/overview',
    redirect: '/admin/operation-reports',
  },
  {
    path: '/admin/operation-reports/users',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '用户增长报告', reportKey: 'users', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/user-usage',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '用户使用统计', reportKey: 'user_usage', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/payments',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '付费转化报告', reportKey: 'payments', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/models',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '模型成本报告', reportKey: 'models', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/invites',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '邀请拉新报告', reportKey: 'invites', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/operation-reports/redemptions',
    component: () => import('@/views/AdminOperationReports'),
    meta: { title: '兑换码效果报告', reportKey: 'redemptions', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/tasks',
    component: () => import('@/views/AdminTasks'),
    props: { taskScope: 'normal' },
    meta: { title: '任务列表', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/users',
    component: () => import('@/views/AdminUsers'),
    meta: { title: '用户管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/administrators',
    component: () => import('@/views/AdminAdministrators'),
    meta: { title: '管理员管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/invites',
    component: () => import('@/views/AdminInvites'),
    meta: { title: '邀请统计', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/redemption',
    component: () => import('@/views/AdminRedemption'),
    meta: { title: '兑换码管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/feedback',
    component: () => import('@/views/AdminFeedback'),
    meta: { title: '用户反馈', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/announcements',
    component: () => import('@/views/AdminAnnouncements'),
    meta: { title: '公告管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/credits',
    component: () => import('@/views/AdminCredits'),
    meta: { title: '用户积分', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/credit-ledger',
    component: () => import('@/views/AdminCreditLedger'),
    meta: { title: '积分变动明细', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/payment-orders',
    component: () => import('@/views/AdminPaymentOrders'),
    meta: { title: '支付订单', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/credit-packages',
    component: () => import('@/views/AdminCreditPackages'),
    meta: { title: '积分加油包', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/storage-packages',
    component: () => import('@/views/AdminStoragePackages.vue'),
    meta: { title: '存储扩容包', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/membership-plans',
    component: () => import('@/views/AdminMemberSettings'),
    meta: { title: '会员设置', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/member-rights',
    redirect: '/admin/membership-plans',
  },
  {
    path: '/admin/app-cases',
    component: () => import('@/views/AdminAppCases'),
    meta: { title: '案例维护', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/rag/materials',
    component: () => import('@/views/AdminRagMaterials'),
    meta: { title: 'RAG素材库', requiresAdmin: true, requiredPlatformRole: 'rag_admin', adminLayout: true },
  },
  {
    path: '/admin/rag/deep-analysis',
    component: () => import('@/views/AdminRagDeepAnalysis'),
    meta: { title: '深度分析', requiresAdmin: true, requiredPlatformRole: 'rag_admin', adminLayout: true },
  },
  {
    path: '/admin/rag/tasks',
    component: () => import('@/views/AdminTasks'),
    props: { taskScope: 'rag' },
    meta: { title: 'RAG任务', requiresAdmin: true, requiredPlatformRole: 'rag_admin', adminLayout: true },
  },
  {
    path: '/admin/rag/search',
    component: () => import('@/views/AdminRagSearch'),
    meta: { title: 'RAG检索测试', requiresAdmin: true, requiredPlatformRole: 'rag_admin', adminLayout: true },
  },
  {
    path: '/admin/tool-apps',
    component: () => import('@/views/AdminToolApps'),
    meta: { title: '工具配置', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/applications',
    component: () => import('@/views/AdminApplications'),
    meta: { title: '应用设置', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/community',
    component: () => import('@/views/AdminCommunity'),
    meta: { title: '社区管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/system-config',
    component: () => import('@/views/AdminSystemConfig'),
    meta: { title: '积分活动', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/model-catalog',
    component: () => import('@/views/AdminPipelineCatalog'),
    meta: { title: '模型线路管理', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/callback-lanes',
    component: () => import('@/views/AdminPipelineCallbackLanes'),
    meta: { title: '回调并发控制', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/pipeline-capacity',
    component: () => import('@/views/AdminPipelineCapacity'),
    meta: { title: '流水线容量诊断', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/model-families',
    component: () => import('@/views/AdminModelFamilies'),
    meta: { title: '模型大类配置', requiresAdmin: true, adminLayout: true },
  },
  {
    path: '/admin/model-usage',
    component: () => import('@/views/AdminModelUsageRecords'),
    meta: { title: '模型用量记录', requiresAdmin: true, adminLayout: true },
  },
]

const router = new VueRouter({
  mode: 'history',
  routes
})
const originalPush = router.push
router.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

router.beforeEach(async (to, from, next) => {
  const requiresAdmin = to.matched.some(record => record.meta && record.meta.requiresAdmin)
  const requiresAuth = requiresAdmin || to.matched.some(record => record.meta && record.meta.requiresAuth)
  if (requiresAuth) {
    if (!authState.ready) {
      await restoreSession()
    }
    const isAuthenticated = !!(authState.session && authState.session.id)
    if (!isAuthenticated) {
      next('/home')
      return
    }
  }
  if (requiresAdmin) {
    const user = authState.session && authState.session.user
    const requiredPlatformRole = to.matched
      .map(record => record.meta && record.meta.requiredPlatformRole)
      .find(Boolean) || ''
    if (!canAccessAdminRoute(user, requiredPlatformRole)) {
      next(adminDefaultPath(user))
      return
    }
  }
  next()
})

router.afterEach((to) => {
  const pageTitle = to.meta && to.meta.title ? `${to.meta.title} · ${appTitle}` : appTitle
  document.title = pageTitle
})

export default router
